import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { formatDate } from "../utils/dateUtils";

// Renders a bureau-scoped audit feed for a single resource. Used inside
// EditUserModal so members can see "who touched this profile" before they
// save their own changes. Reusable for Referral / InterestRequest later.
//
// Props:
//   resource:   "User" | "Referral" | "InterestRequest" | "BureauMember"
//   resourceId: ObjectId string
//   limit:      max entries (default 10)
const actionLabel = (a) =>
  ({
    create: "created",
    update: "edited",
    delete: "deleted",
    disable: "disabled",
    approved: "approved",
    rejected: "rejected",
  }[a] || a);

const formatWhen = (at) => {
  try {
    const d = new Date(at);
    return `${formatDate(d)} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return "";
  }
};

const RecentActivity = ({ resource, resourceId, limit = 10 }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!resource || !resourceId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    apiFetch(`/audit/${resource}/${resourceId}?limit=${limit}`)
      .then((res) => {
        if (cancelled) return;
        setEntries(res?.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load activity");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resource, resourceId, limit]);

  if (!resourceId) return null;

  return (
    <div className="border-t border-[var(--color-border)] pt-3 mt-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-2">
        Recent activity
      </div>
      {loading && (
        <div className="text-xs text-[var(--color-text-muted)]">Loading…</div>
      )}
      {error && (
        <div className="text-xs text-[var(--color-danger-text)]">{error}</div>
      )}
      {!loading && !error && entries.length === 0 && (
        <div className="text-xs text-[var(--color-text-muted)]">
          No activity recorded yet.
        </div>
      )}
      {!loading && !error && entries.length > 0 && (
        <ul className="space-y-1.5 text-xs">
          {entries.map((e) => {
            const who =
              e.member?.fullName || e.memberName || "Unknown member";
            const role = e.member?.role;
            const fields = Array.isArray(e.diff?.fields)
              ? e.diff.fields.slice(0, 4).join(", ")
              : null;
            return (
              <li
                key={e._id}
                className="flex items-baseline justify-between gap-3"
              >
                <span className="text-[var(--color-text)]">
                  <span className="font-medium">{who}</span>
                  {role && (
                    <span className="ml-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                      ({role})
                    </span>
                  )}{" "}
                  {actionLabel(e.action)}
                  {fields && (
                    <span className="text-[var(--color-text-muted)]">
                      {" "}
                      · {fields}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-[10px] text-[var(--color-text-muted)]">
                  {formatWhen(e.at)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default RecentActivity;
